import { getCspNonce } from '@/lib/csp-nonce';
import { forwardRef, useEffect, useImperativeHandle, useRef, type CSSProperties } from 'react';
import {
  createEditor,
  PALETTE,
  type Asset,
  type EditorHandle,
  type StorageLimits,
  type StorageProvider,
  type StorageUploadInfo,
} from '@seliseblocks/mailcraft';
import { ModuleName } from '@/constant/modules.constants';
import {
  getObjects,
  isStorageImageFile,
  uploadPublicFile,
} from '@/lib/api/services/storage.service';
import type { StorageFileItem } from '@/lib/api/types/storage.types';
import { CAMPAIGN_MERGE_TAG_VARIABLES } from '../../utils/campaign-content.utils';
import { cn } from '@/lib/utils';

export type MailcraftEmailBuilderHandle = {
  exportHtml: () => string;
  loadHtml: (html: string, name?: string) => void;
};

type MailcraftEmailBuilderProps = {
  html?: string;
  name?: string;
  className?: string;
  style?: CSSProperties;
  onChange?: () => void;
  /**
   * `design` matches campaign-editor.html: fixed 660px height + 1212px min-width
   * inside an overflow-x-auto shell so the inspector is not clipped.
   * `fluid` fills the host (wizard embeds).
   */
  layout?: 'design' | 'fluid';
};

/** Matches product primary-600 / prototype mailcraft-setup.js. */
const BRAND_ACCENT = '#0066B2';
/** Media Library upload cap — enforced by MailCraft before provider.upload. */
const MAX_UPLOAD_BYTES = 1 * 1024 * 1024;

/**
 * Condition / Loop emit Handlebars our send path does not evaluate (MVP-3).
 * PALETTE is documented as an internal whose shape may change — fail soft.
 * @see https://mailcraft.seliseblocks.com/DOCS.html
 */
const MVP3_BLOCKS = new Set(['condition', 'loop']);

const hideMvp3Blocks = (): void => {
  try {
    if (!Array.isArray(PALETTE)) return;
    for (let i = PALETTE.length - 1; i >= 0; i -= 1) {
      const entry = PALETTE[i];
      if (entry?.t && MVP3_BLOCKS.has(entry.t)) {
        PALETTE.splice(i, 1);
      }
    }
  } catch {
    // Pre-send checks remain the backstop if the palette shape changes.
  }
};

// MailCraft's own autosave keys (src/core/editor-core.js: STORAGE_KEY, TAB_KEY_PREFIX,
// TAB_ID_KEY) — not exported by the package, so mirrored here to let the host clear
// them. Bump if a MailCraft upgrade changes these.
const MAILCRAFT_AUTOSAVE_KEY = 'mailcraft.v3';
const MAILCRAFT_TAB_KEY_PREFIX = `${MAILCRAFT_AUTOSAVE_KEY}.tab.`;
const MAILCRAFT_TAB_ID_KEY = 'mailcraft.tab';

const STORAGE_LIMITS: StorageLimits = {
  maxBytes: MAX_UPLOAD_BYTES,
  accept: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
  allowSvg: false,
};

const storageFileToAsset = (file: StorageFileItem): Asset | null => {
  const url = (file.url || '').trim();
  if (!url) return null;

  return {
    id: file.itemId || url,
    name: (file.name || file.systemName || 'image').trim() || 'image',
    url,
    folder: '',
    folderId: file.parentDirectoryID || '',
    w: 0,
    ht: 0,
    size: typeof file.sizeInBytes === 'number' ? file.sizeInBytes : 0,
  };
};

type CloudStorageProvider = StorageProvider & {
  /** Allows list() to hit get-objects (off until Media Library opens). */
  enableListing: () => void;
};

/**
 * Media Library backed by Blocks cloud storage.
 * Uploads go through pre-signed PUT (`uploadPublicFile`). Listing is deferred
 * until the Media Library is opened so edit-template mount does not spam APIs.
 *
 * @see https://mailcraft.seliseblocks.com/DOCS.html#accept-image-uploads
 */
const createCloudSessionStorageProvider = (): CloudStorageProvider => {
  const sessionAssets: Asset[] = [];
  let listingEnabled = false;

  return {
    limits: STORAGE_LIMITS,
    enableListing: () => {
      listingEnabled = true;
    },
    async list({ query, signal }) {
      // MailCraft calls list() when the storage provider is attached on mount.
      // Skip network until the user opens the Media Library.
      if (!listingEnabled) {
        return { items: [], cursor: null };
      }

      if (signal?.aborted) {
        throw new DOMException('Listing aborted', 'AbortError');
      }

      const search = (query || '').trim();

      const response = await getObjects({
        limit: 50,
        search: search || undefined,
      });

      if (signal?.aborted) {
        throw new DOMException('Listing aborted', 'AbortError');
      }

      if (response.errors && Object.keys(response.errors).length > 0) {
        const errorMessage = Object.values(response.errors).join(', ');
        throw new Error(errorMessage || 'Failed to load media library files.');
      }

      const remoteAssets = (response.data ?? [])
        .filter(isStorageImageFile)
        .map(storageFileToAsset)
        .filter((asset): asset is Asset => Boolean(asset));

      const remoteIds = new Set(remoteAssets.map((asset) => asset.id));
      const remoteUrls = new Set(remoteAssets.map((asset) => asset.url));
      const pendingSession = sessionAssets.filter(
        (asset) => !remoteIds.has(asset.id) && !remoteUrls.has(asset.url)
      );
      const searchLower = search.toLowerCase();
      const pendingFiltered = searchLower
        ? pendingSession.filter((asset) => asset.name.toLowerCase().includes(searchLower))
        : pendingSession;

      return { items: [...pendingFiltered, ...remoteAssets], cursor: null };
    },
    async upload(file: File, info: StorageUploadInfo): Promise<Asset> {
      listingEnabled = true;

      if (info.signal?.aborted) {
        throw new DOMException('Upload aborted', 'AbortError');
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        throw new Error('Image must be 1 MB or smaller.');
      }

      const { fileId, fileUrl } = await uploadPublicFile(file, ModuleName.DefaultCloud);

      if (info.signal?.aborted) {
        throw new DOMException('Upload aborted', 'AbortError');
      }
      if (!fileUrl) {
        throw new Error('Upload succeeded but no image URL was returned.');
      }

      const asset: Asset = {
        id: fileId || `cloud-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        url: fileUrl,
        folder: '',
        folderId: info.folderId || '',
        w: info.width || 0,
        ht: info.height || 0,
        size: file.size,
      };
      sessionAssets.unshift(asset);
      return asset;
    },
  };
};

type MailcraftCore = {
  openLibrary?: (assetTarget?: unknown) => void;
  refreshAssets?: (opts?: { append?: boolean }) => Promise<void>;
  setState?: (patch: Record<string, unknown>) => void;
  removeAsset?: (asset: unknown) => Promise<void>;
  state?: {
    assetsLoading?: boolean;
    assetsError?: string | null;
  };
};

type MailcraftEditorElement = {
  core?: MailcraftCore;
  shadowRoot?: ShadowRoot | null;
  assetGrid?: HTMLElement;
  assetStatus?: HTMLElement;
  assetEmpty?: HTMLElement;
  renderLibraryModal?: () => void;
};

const LIBRARY_SKELETON_COUNT = 6;
const LIBRARY_HOST_STYLE_ID = 'campaign-media-library-host';

/** Fetch media only when the library opens (not on editor mount). */
const patchLibraryOpenToFetchOnDemand = (
  editorElement: MailcraftEditorElement | null,
  storageProvider: CloudStorageProvider
) => {
  const core = editorElement?.core;
  if (!core || typeof core.openLibrary !== 'function' || typeof core.refreshAssets !== 'function') {
    return;
  }

  core.openLibrary = (assetTarget?: unknown) => {
    storageProvider.enableListing();
    core.setState?.({ libraryOpen: true, assetTarget: assetTarget || null });
    void core.refreshAssets?.();
  };
};

const ensureMediaLibraryHostStyles = (shadow: ShadowRoot) => {
  if (shadow.getElementById(LIBRARY_HOST_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = LIBRARY_HOST_STYLE_ID;
  const nonce = getCspNonce();
  if (nonce) style.nonce = nonce;
  style.textContent = `
    /* Hide MailCraft DEL — cloud media is not deleted from this UI. */
    [data-campaign-asset-grid] > div > div:last-child > div:last-child > span:last-child {
      display: none !important;
    }

    @keyframes campaign-lib-skel-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.55; }
    }

    .campaign-lib-skel-tile {
      border: 1px solid var(--ed-line);
      border-radius: var(--ed-radius-sm);
      overflow: hidden;
      background: var(--ed-panel-2);
      pointer-events: none;
    }

    .campaign-lib-skel-preview,
    .campaign-lib-skel-line,
    .campaign-thumb-skeleton {
      background: #edeef0 !important;
      animation: campaign-lib-skel-pulse 1.2s ease-in-out infinite;
    }

    .campaign-lib-skel-preview {
      width: 100%;
      height: 120px;
      border-bottom: 1px solid var(--ed-line);
    }

    .campaign-lib-skel-meta {
      padding: 8px 9px;
    }

    .campaign-lib-skel-line {
      display: block;
      height: 10px;
      border-radius: 4px;
      margin-bottom: 6px;
    }

    .campaign-lib-skel-line.short {
      width: 42%;
      height: 8px;
      margin-bottom: 0;
    }

    .campaign-thumb-skeleton {
      background-image: none !important;
    }
  `;
  shadow.appendChild(style);
};

const createLibrarySkeletonTile = (): HTMLElement => {
  const tile = document.createElement('div');
  tile.className = 'campaign-lib-skel-tile';
  tile.setAttribute('aria-hidden', 'true');
  tile.innerHTML = `
    <div class="campaign-lib-skel-preview"></div>
    <div class="campaign-lib-skel-meta">
      <span class="campaign-lib-skel-line"></span>
      <span class="campaign-lib-skel-line short"></span>
    </div>
  `;
  return tile;
};

const extractBackgroundImageUrl = (value: string): string => {
  const match = value.match(/url\((['"]?)(.*?)\1\)/i);
  return match?.[2]?.trim() || '';
};

/** Pulse skeleton on each tile preview until the image URL finishes loading. */
const wireThumbnailSkeleton = (preview: HTMLElement) => {
  if (preview.dataset.campaignThumbSkel === '1') return;
  preview.dataset.campaignThumbSkel = '1';

  const backgroundImage = preview.style.backgroundImage || '';
  const url = extractBackgroundImageUrl(backgroundImage);
  if (!url || url === 'none') return;

  preview.classList.add('campaign-thumb-skeleton');
  preview.style.backgroundImage = 'none';

  const finish = () => {
    preview.style.backgroundImage = backgroundImage;
    preview.classList.remove('campaign-thumb-skeleton');
  };

  const img = new Image();
  img.onload = finish;
  img.onerror = finish;
  img.src = url;
};

/**
 * Host patches for Media Library UX:
 * - hide DEL
 * - skeleton cards while the file list loads
 * - skeleton thumbnails while each image paints
 */
const enhanceMediaLibraryUi = (editorElement: MailcraftEditorElement | null) => {
  if (!editorElement) return;

  const shadow = editorElement.shadowRoot;
  if (shadow) ensureMediaLibraryHostStyles(shadow);

  editorElement.assetGrid?.setAttribute('data-campaign-asset-grid', '');

  if (editorElement.core) {
    editorElement.core.removeAsset = () => Promise.resolve();
  }

  const originalRender = editorElement.renderLibraryModal?.bind(editorElement);
  if (!originalRender) return;

  editorElement.renderLibraryModal = () => {
    originalRender();

    const grid = editorElement.assetGrid;
    if (!grid) return;

    const loading = Boolean(editorElement.core?.state?.assetsLoading);
    const hasError = Boolean(editorElement.core?.state?.assetsError);
    const realTiles = Array.from(grid.children).filter(
      (node) => node instanceof HTMLElement && !node.classList.contains('campaign-lib-skel-tile')
    );

    if (loading && realTiles.length === 0 && !hasError) {
      for (let i = 0; i < LIBRARY_SKELETON_COUNT; i += 1) {
        grid.appendChild(createLibrarySkeletonTile());
      }
      if (editorElement.assetStatus) editorElement.assetStatus.style.display = 'none';
      if (editorElement.assetEmpty) editorElement.assetEmpty.style.display = 'none';
      return;
    }

    realTiles.forEach((tile) => {
      const preview = tile.firstElementChild;
      if (!(preview instanceof HTMLElement) || preview.getAttribute('role') !== 'img') return;
      wireThumbnailSkeleton(preview);
    });
  };
};

/**
 * MailCraft host — HTML in / HTML out via `createEditor` + `exportHtml` /
 * `loadTemplate`, per https://mailcraft.seliseblocks.com/README.html
 */
export const MailcraftEmailBuilder = forwardRef<
  MailcraftEmailBuilderHandle,
  MailcraftEmailBuilderProps
>(({ html = '', name = 'Draft', className, style, onChange, layout = 'fluid' }, ref) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<EditorHandle | null>(null);
  const onChangeRef = useRef(onChange);
  const loadedHtmlRef = useRef<string | null>(null);
  onChangeRef.current = onChange;

  useImperativeHandle(ref, () => ({
    exportHtml: () => {
      try {
        return editorRef.current?.exportHtml() ?? html;
      } catch {
        return html;
      }
    },
    loadHtml: (nextHtml, nextName) => {
      try {
        editorRef.current?.loadTemplate({
          name: nextName ?? name,
          html: nextHtml,
        });
        loadedHtmlRef.current = nextHtml;
      } catch {
        // ignore
      }
    },
  }));

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    hideMvp3Blocks();

    // MailCraft has its own tab-scoped autosave draft in localStorage and restores
    // it on mount whenever the host hasn't already called loadTemplate/importHtml/
    // loadDoc (its internal `docSetByHost` flag) — and `createEditor` itself only
    // does that when `html` is non-empty (see its source: `if (o.html?.trim())`).
    // So a blank `html` here silently inherits whatever was last edited in this
    // browser tab — a different template, or a campaign — instead of starting
    // empty. When we actually want blank, clear MailCraft's own keys first so its
    // restore finds nothing and falls back to its built-in starter document.
    if (!html.trim()) {
      try {
        localStorage.removeItem(MAILCRAFT_AUTOSAVE_KEY);
        const tabId = sessionStorage.getItem(MAILCRAFT_TAB_ID_KEY);
        if (tabId) localStorage.removeItem(MAILCRAFT_TAB_KEY_PREFIX + tabId);
      } catch {
        // ignore — storage may be unavailable (private mode, quota, etc.)
      }
    }

    const storageProvider = createCloudSessionStorageProvider();
    const editor = createEditor(host, {
      html,
      name,
      variables: CAMPAIGN_MERGE_TAG_VARIABLES,
      accent: BRAND_ACCENT,
      uiFont: 'inherit',
      toolbar: 'undo,redo,code,export,device,status',
      footer: false,
      replace: true,
      storageLimits: STORAGE_LIMITS,
      storageProvider,
      onChange: () => {
        onChangeRef.current?.();
      },
    });

    // Limits only — createEditor already set storageProvider; reassigning it
    // triggers another refreshAssets() on mount.
    editor.element.storageLimits = STORAGE_LIMITS;
    const editorEl = editor.element as unknown as MailcraftEditorElement;
    patchLibraryOpenToFetchOnDemand(editorEl, storageProvider);
    enhanceMediaLibraryUi(editorEl);

    editorRef.current = editor;
    loadedHtmlRef.current = html;

    // Shortcuts bind on window; stop Backspace/Delete/⌘E when focus is outside
    // the editor so they do not mutate the canvas from the rest of the page.
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const inEditor = Boolean(target?.closest?.('mailcraft-editor'));
      if (inEditor) return;
      const isField = Boolean(target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName || ''));
      if (!isField && (event.key === 'Backspace' || event.key === 'Delete')) {
        event.stopPropagation();
      }
      if ((event.metaKey || event.ctrlKey) && (event.key === 'e' || event.key === 'E')) {
        event.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      try {
        editor.destroy();
      } catch {
        // ignore
      }
      editorRef.current = null;
      loadedHtmlRef.current = null;
    };
    // Mount once; later HTML updates go through loadTemplate below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (loadedHtmlRef.current === html) return;
    try {
      editor.loadTemplate({ name, html });
      loadedHtmlRef.current = html;
    } catch {
      // ignore
    }
  }, [html, name]);

  return (
    <div
      ref={hostRef}
      className={cn(
        'mailcraft-host w-full',
        layout === 'design'
          ? 'h-[660px] min-w-[1212px]'
          : 'h-full min-h-[640px] min-w-0 max-w-full overflow-hidden',
        className
      )}
      style={layout === 'design' ? style : { height: '70vh', ...style }}
    />
  );
});

MailcraftEmailBuilder.displayName = 'MailcraftEmailBuilder';
